import { motion } from 'framer-motion';
import { ArrowDown, Mail, Download, Github, Linkedin, Instagram } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import heroPortrait from '../assets/RESTU AGIL YULI ARJUN_S2025101502.png';

export default function Hero() {
  const { t } = useLanguage();
  const companyUrl = 'https://www.telkomsigma.co.id/';

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '3s' }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-150 h-150 bg-accent-600/10 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(128,128,128,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(128,128,128,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="section-container relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="hidden lg:block text-muted text-lg mb-4 font-mono"
            >
              &lt;{t.hero.greeting}&gt;
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight mb-2">
                {t.hero.im} <span className="gradient-text">Restu Agil</span>
              </h1>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-body mb-4">
                Yuli Arjun
              </h2>
            </motion.div>

            {/* Role Tags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap justify-center lg:justify-start gap-3 mt-4 mb-6"
            >
              {t.hero.roles.map((role) => {
                const isCompanyRole = role.toLowerCase().includes('telkomsigma');

                if (isCompanyRole) {
                  return (
                    <a
                      key={role}
                      href={companyUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 rounded-full glass text-sm text-body font-medium hover:text-accent-400 transition-colors"
                    >
                      {role}
                    </a>
                  );
                }

                return (
                  <span
                    key={role}
                    className="px-4 py-2 rounded-full glass text-sm text-body font-medium"
                  >
                    {role}
                  </span>
                );
              })}
            </motion.div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="max-w-xl text-muted text-lg leading-relaxed mb-8"
            >
              {t.hero.desc.p1}{' '}
              <a
                href={companyUrl}
                target="_blank"
                rel="noreferrer"
                className="text-accent-400 font-medium hover:text-accent-300 transition-colors"
              >
                {t.hero.desc.company}
              </a>{' '}
              {t.hero.desc.p2}{' '}
              <span className="text-cyan-400 font-medium">{t.hero.desc.ai}</span>
              {t.hero.desc.p3}{' '}
              <span className="text-accent-400 font-medium">{t.hero.desc.db}</span>{' '}
              {t.hero.desc.p4}{' '}
              <span className="text-cyan-400 font-medium">{t.hero.desc.dl}</span>.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8"
            >
              <a
                href="mailto:restuagil.ya@gmail.com"
                className="group flex items-center gap-2 px-7 py-3.5 rounded-xl bg-linear-to-r from-accent-500 to-cyan-500 text-white font-semibold hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300 hover:-translate-y-1"
              >
                <Mail size={18} />
                {t.hero.emailMe}
              </a>
              <a
                href="/assets/CV/CV%20Restu%20Agil%20Yuli%20Arjun%20-%202025%20en.pdf"
                download="CV Restu Agil Yuli Arjun - 2025 en.pdf"
                className="group flex items-center gap-2 px-7 py-3.5 rounded-xl glass glass-hover text-heading font-semibold hover:-translate-y-1"
              >
                <Download size={18} />
                {t.hero.downloadCV}
              </a>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex justify-center lg:justify-start gap-4"
            >
              {[
                { icon: Github, href: 'https://github.com/RestuAgilYA', label: 'GitHub' },
                { icon: Linkedin, href: 'https://www.linkedin.com/in/restuagilya/', label: 'LinkedIn' },
                { icon: Instagram, href: 'https://www.instagram.com/_restuagil', label: 'Instagram' },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-12 h-12 rounded-xl glass glass-hover flex items-center justify-center text-muted hover:text-heading"
                >
                  <Icon size={20} />
                </a>
              ))}
            </motion.div>
          </div>

          {/* Right - Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="shrink-0 order-1 lg:order-2"
          >
            <div className="relative">
              <motion.p
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.55, delay: 0.2 }}
                className="lg:hidden absolute top-9 sm:top-10 left-1/2 -translate-x-[145%] sm:-translate-x-[138%] text-muted text-[11px] sm:text-sm font-mono whitespace-nowrap z-30 px-2.5 py-1 rounded-full glass"
              >
                &lt;{t.hero.greeting}&gt;
              </motion.p>

              {/* Gradient glow */}
              <div className="absolute -inset-8 bg-linear-to-br from-accent-500/25 via-cyan-500/15 to-accent-600/20 rounded-full blur-3xl" />

              {/* Decorative rings */}
              <div className="absolute -inset-4 rounded-full border-2 border-dashed border-accent-500/15 animate-spin-slow" />
              <div
                className="absolute -inset-8 rounded-full border border-cyan-500/10 animate-spin-slow"
                style={{ animationDirection: 'reverse', animationDuration: '12s' }}
              />

              {/* Photo */}
              <div className="relative w-72 h-88 sm:w-88 sm:h-104 md:w-md md:h-128 flex items-end justify-center">
                <img
                  src={heroPortrait}
                  alt="Restu Agil Yuli Arjun"
                  width="880"
                  height="1168"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-contain object-bottom drop-shadow-[0_0_40px_rgba(99,102,241,0.35)] relative z-10"
                  style={{
                    maskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 10%, black 100%)',
                  }}
                />
              </div>

              {/* Badge */}
              <a
                href={companyUrl}
                target="_blank"
                rel="noreferrer"
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 rounded-xl glass text-sm font-semibold text-heading shadow-lg z-20 hover:text-accent-400 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  {t.hero.badge}
                </span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.a
        href="#about"
        aria-label="Scroll to About section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-faint hover:text-accent-400 transition-colors"
      >
        <span className="sr-only">Scroll to About section</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowDown size={24} />
        </motion.div>
      </motion.a>
    </section>
  );
}
